import { useCallback, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
  TextField,
  Chip,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { Colors } from "../../../Styles/Colors";
import CommonGridHeader, { AppointmentCard, CommonPatientDetails, ConfirmPopup, ReasonPopupWithRefund } from "../../../Components/Common Components/CommonComponents";
import { AmountFormat, ContentInTooltip } from "../../../Components/CommonFunctions/CommonFunctions";

const DATE_FORMAT = "YYYY-MM-DD";

function formatDateTime(str) {
  if (!str) return "-";
  const d = dayjs(str, ["DD-MM-YYYY hh:mm A", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD"]);
  return d.isValid() ? d.format("DD-MM-YYYY | hh:mm A") : str;
}

function roomDetails(row) {
  const floorStr = row.floor != null && row.floor !== "" ? (row.floor === "0" ? "Ground" : `${row.floor}`) : "";
  const parts = [row.block_name, floorStr, row.ward_name, row.room_number].filter(Boolean);
  return parts.length ? parts.join(" / ") : "-";
}

export default function ReservationList(props) {
  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs().endOf("month"));
  const [statusFilter, setStatusFilter] = useState(""); // "" | "Reserved" | "Completed" | "Cancelled"
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({ total_count: 0, total_reserved_count: 0, total_completed_count: 0, total_cancelled_count: 0 });
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [errorMsg, setErrorMsg] = useState(false);
  const [errorMsgText, setErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelRow, setCancelRow] = useState(null);
  const [refundPopupOpen, setRefundPopupOpen] = useState(false);

  const fromStr = fromDate.format(DATE_FORMAT);
  const toStr = toDate.format(DATE_FORMAT);
  
  const statusColorMap = {
    "Reserved": { bg: "#04B7B1", color: "#fff" },
    "Completed": { bg: "#1E972A", color: "#fff" },
    "Cancelled": { bg: "#B8170D", color: "#fff" },
  };

  const fetchList = useCallback(() => {
    setLoading(true);
    const statusQs = statusFilter ? `&status=${statusFilter}` : "";
    RestAPIService.getAll(`${Serviceurls.IP_PATIENT_RESERVE_ROOM}?from_date=${fromStr}&to_date=${toStr}${statusQs}`)
      .then((res) => {
        if (res?.data?.status === "success") {
          const data = (res?.data?.data || []).map((row, idx) => ({
            ...row,
            id: row.id ?? idx,
          }));
          setList(data);
          setCounts(res?.data?.count || { total_count: 0, total_reserved_count: 0, total_completed_count: 0, total_cancelled_count: 0 });
        } else {
          setErrorMsgText(res?.data?.message || "Failed to load");
          setErrorMsg(true);
        }
      })
      .catch((e) => {
        setErrorMsgText(e?.response?.data?.message || e?.message || "Error");
        setErrorMsg(true);
      })
      .finally(() => setLoading(false));
  }, [fromStr, toStr, statusFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const msgClose = () => {
    setErrorMsg(false);
    setSuccessMsg(false);
    setErrorMsgText("");
    setSuccessMsgText("");
  };

  const postCancel = (row, refundState) => {
    if (!row?.id) return;
    setLoading(true);
    const advance = +(row.total_advance_amount || 0);
    const payload = {
      reservation_id: row.id,
      status: "Cancelled",
      // modify: true,
      cancel_reason: refundState?.comments || "",
      bed_id: row.bed_id ?? null,
      start_date: row.start_date || null,
      end_date: row.end_date || null,
      patient_id: row.patient_id ?? null,
      allocated_for: "Patient",
      maintenance_id: null,
      is_attender: false,
      doctor_id: row.doctor_id ?? null,
      receipt_id: null,
      receipt_for: advance > 0 && refundState ? "Refund" : "Cancelled",
      amount_received: 0,
      amount_cash: 0,
      amount_card: 0,
      upi_amount: 0,
      bank_transfer_amount: 0,
      cheque_amount: 0,
      card_ref_no: "",
      upi_ref_no: "",
      bank_ref_no: "",
      cheque_ref_no: "",
      payment_mode: [],
    };
    if (advance > 0 && refundState) {
      const cashAmt = +refundState.CashAmount || 0;
      const upiAmt = +refundState.UPIAmount || 0;
      const bankAmt = +refundState.BankTfrAmount || 0;
      payload.amount_received = +(cashAmt + upiAmt + bankAmt);
      payload.amount_cash = cashAmt;
      payload.upi_amount = upiAmt;
      payload.bank_transfer_amount = bankAmt;
      payload.upi_ref_no = refundState.UPINumber || "";
      payload.bank_ref_no = refundState.BankTfrNumber || "";
      const mode = [];
      if (refundState.isCash) mode.push(1);
      if (refundState.isUPI) mode.push(3);
      if (refundState.isBankTranfer) mode.push(4);
      payload.payment_mode = mode;
    }
    RestAPIService.create(payload, Serviceurls.IP_PATIENT_RESERVE_ROOM)
      .then((res) => {
        if (res?.data?.status === "success") {
          setSuccessMsgText(res?.data?.message || "Reservation cancelled successfully");
          setSuccessMsg(true);
          setCancelConfirmOpen(false);
          setRefundPopupOpen(false);
          setCancelRow(null);
          fetchList();
        } else {
          setErrorMsgText(res?.data?.message || "Failed to cancel");
          setErrorMsg(true);
        }
      })
      .catch((e) => {
        setErrorMsgText(e?.response?.data?.message || e?.message || "Error");
        setErrorMsg(true);
      })
      .finally(() => setLoading(false));
  };

  const handleCancelClick = () => {
    const row = menuRow;
    handleMenuClose();
    if (!row) return;
    setCancelRow(row);
    setCancelConfirmOpen(true);
  };

  const handleCancelConfirmYes = () => {
    const row = cancelRow;
    if (!row) return;
    const advance = +(row.total_advance_amount || 0);
    if (advance <= 0) {
      postCancel(row, null);
      setCancelConfirmOpen(false);
      setCancelRow(null);
    } else {
      setCancelConfirmOpen(false);
      setRefundPopupOpen(true);
    }
  };

  const handleRefundSubmit = (state) => {
    postCancel(cancelRow, state);
  };

  const renderStatusChip = (status) => {
    const clr = statusColorMap[status] || { bg: "#E0E0E0", color: "#424242" };
    return (
      <Chip label={status} size="small" sx={{ backgroundColor: clr.bg, border: `1px solid ${clr.color}`, px: 1, py: 0.4, borderRadius: "3px", color: clr.color }} />
    );
  };

  const columns = [
    {
      field: "patient_name",
      headerName: "Patient Details",
      flex: 0.18,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ py: 0.5 }}>
          <CommonPatientDetails
            data={{
              ...params.row,
              patient__mobile_number: params.row.mobile_number,
            }}
          />
        </Box>
      ),
    },
    {
      field: "created_by",
      headerName: "Booked Details",
      flex: 0.13,
      minWidth: 140,
      renderCell: (params) => {
        const createdBy = params.row.created_by || "-";
        const createdDate = params.row.created_date ? formatDateTime(params.row.created_date) : "-";
        return  (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {ContentInTooltip(createdBy, 23, "0.95rem")}
            {ContentInTooltip(createdDate, 23, "0.75rem")}
          </Box>
        );  
      },
    },
    {
      field: "start_date",
      headerName: "Check in Date",
      flex: 0.14,
      minWidth: 130,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.85rem" }}>{formatDateTime(params.row.start_date)}</Typography>
      ),
    },
    {
      field: "room_number",
      headerName: "Room Details",
      flex: 0.17,
      minWidth: 140,
      renderCell: (params) => {
        const roomDetailsStr = roomDetails(params.row);
        return (
         ContentInTooltip(roomDetailsStr, 30, "0.85rem")
        );
      },
    },
    {
      field: "end_date",
      headerName: "Check Out Date",
      flex: 0.12,
      minWidth: 130,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.85rem" }}>{formatDateTime(params.row.end_date)}</Typography>
      ),
    },
    {
      field: "remarks",
      headerName: "Remarks",
      flex: 0.15,
      minWidth: 120,
      renderCell: (params) => {
        const remarks = params.row.remarks || "-";
        return (
          remarks.length > 20 ? (
            <Tooltip title={remarks}>
              <Typography sx={{ fontSize: "0.85rem" }}>{remarks.slice(0, 20) + "..."}</Typography>
            </Tooltip>
          ) : (
            <Typography sx={{ fontSize: "0.85rem" }}>{remarks}</Typography>
          )
        );
      },
    },
    {
      field: "total_advance_amount",
      headerName: "Advance Amount",
      flex: 0.08,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.85rem" }}>
          {params.row.total_advance_amount != null ? AmountFormat(params.row.total_advance_amount) : "0"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.08,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.85rem" }}>{renderStatusChip(params.row.status)}</Typography>
      ),
    },  
    {
      field: "action",
      headerName: "Action",
      flex: 0.07,
      minWidth: 90,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          {/* <IconButton size="small" title="View" onClick={(e) => e.stopPropagation()}>
            <DescriptionOutlinedIcon fontSize="small" />
          </IconButton> */}
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: "85vh", width: "100%", backgroundColor: Colors.Background, p: 1 }}>
        <Box style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Month"
              value={fromDate}
              onChange={(d) => {  
                if (d) {
                  setFromDate(d.startOf("month"));
                  setToDate(d.endOf("month"));
                }
              }}
              views={["year", "month"]}
              inputFormat="MMMM YYYY"
              renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
            />
          </LocalizationProvider>
          <Box style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
            <AppointmentCard onClick={() => setStatusFilter("")} count={counts.total_count ?? 0} label="All" color="#04B7B1" isSelectedCard={statusFilter === ""} />
            <AppointmentCard onClick={() => setStatusFilter("Reserved")} count={counts.total_reserved_count ?? 0} label="Reserved" color="#0461B7" isSelectedCard={statusFilter === "Reserved"} />
            <AppointmentCard onClick={() => setStatusFilter("Completed")} count={counts.total_completed_count ?? 0} label="Completed" color="#1E972A" isSelectedCard={statusFilter === "Completed"} />
            <AppointmentCard onClick={() => setStatusFilter("Cancelled")} count={counts.total_cancelled_count ?? 0} label="Cancelled" color="#B8170D" isSelectedCard={statusFilter === "Cancelled"} />
          </Box>
        </Box>

      <Paper sx={{ height: "calc(85vh - 140px)", minHeight: 300 }}>
        <DataGrid
          rows={list}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 20, 30]}
          pagination
          paginationMode="client"
          density="comfortable"
          headerHeight={45}
          rowHeight={50}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-columnHeaders": { minHeight: 45, maxHeight: 45 },
          }}
          components={{
            Toolbar: CommonGridHeader.CommonToolBar,
            NoRowsOverlay: () => (
              <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
                No Records To Be Shown
              </Box>
            ),
          }}
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          disabled={menuRow?.status === "Cancelled"}
          onClick={handleCancelClick}
        >
          Cancel
        </MenuItem>
        <MenuItem
          disabled={menuRow?.status === "Cancelled"}
          onClick={() => {
            const row = menuRow;
            handleMenuClose();
            if (row && props?.history) {
              props.history.push({ pathname: "/IPHome/RoomBooking", state: { modifyReservation: row } });
            }
          }}
        >
          Modify
        </MenuItem>
      </Menu>

      {cancelConfirmOpen && (
        <ConfirmPopup
          cnfTitle="Cancel Reservation"
          cnfContent="Are you sure you want to cancel this reservation?"
          cnfAlertPopupClose={() => {
            setCancelConfirmOpen(false);
            setCancelRow(null);
          }}
          cnfData={handleCancelConfirmYes}
          btnText1="No"
          btnText2="Yes"
          btnType="primary"
        />
      )}

      {refundPopupOpen && cancelRow && +(cancelRow.total_advance_amount || 0) > 0 && (
        <ReasonPopupWithRefund
          title={`Refund advance - Total amount ${AmountFormat(cancelRow.total_advance_amount)}`}
          AlertPopupClose={() => {
            setRefundPopupOpen(false);
            setCancelRow(null);
          }}
          label="Reason for cancel (optional)"
          btntext="Close"
          btnvarient="outlined"
          btncolor="error"
          btntext1="Proceed Refund & Cancel"
          btnvarient1="contained"
          payableAmount={+(cancelRow.total_advance_amount || 0)}
          fromReservation={true}
          optionalReason={true}
          IPRefundPost={handleRefundSubmit}
          ShowAdvance={false}
        />
      )}

      {errorMsg && <ToastMsg severity="error" msg={errorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      {loading && <Loader />}
    </Box>
  );
}
